import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AddBalancePopup } from '@components/AddBalancePopup';
import { AddCreditCardPopup } from '@components/AddCreditCardPopup';
import { AddressValidationPopup } from '@components/AddressValidationPopup';
import { LoaderBar } from '@components/LoaderBar';
import { PageHeaderText, ShadowBox, StyledToggle } from '@elements/Common';
import {
  ManualShipCard,
  createShippingSchema,
  findPlanOfItem,
  hasAvailableShipmentItems,
  isOpen,
  mergeInto,
  moveItem,
  nextOpenPlan,
  attributeBoxes,
  quotedBoxes,
  reconcilePlans,
  resolveDefaultShippingSpeed,
  setPlanProvider,
  setPlanStatus,
  splitItem,
  type FulfillmentItem,
  type PackageBox,
  type PlannerProvider,
  type ShipmentPlan,
  type ShipmentStatus,
  type ShipmentView,
  type ShippingFormValues,
} from '@features/order-details';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  OrderContent,
  OrderShippingLabel,
} from '@skupreme/api-contracts/core';
import { orderDetailsHook } from '@hooks/order';
import {
  addressValidationHook,
  rateHook,
  shippingLabelFPHook,
  shippingLabelMetadataHook,
  shippingOrderFulfillMetadataHook,
  useHighestQuantityAliasInventory,
  useShippingLabelsManualShipOrder,
} from '@hooks/shipping-label';
import { settlePendingWalletLabel } from './pendingWalletLabel';
import { PrintLabelPopup } from '@page-components/order/PrintLabelPopup';
import {
  Customer,
  ItemOrdered,
  Notes,
  Shipping,
  Summary,
} from '@page-components/order/ShippingLabelComponents';
import { watchFormFields } from '@page-components/order/common';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  ErrorMessageConstant,
  OrderStatus,
  StorageIntegrationTypes,
} from '@utility/constants/common';
import ShippingLabelService from '@utility/services/shipping-label';
import { ToastMessage } from '@utility/toast';
import { useAuth } from '@contexts/auth';
import { useDebouncedEffect, useDebouncedMemo } from '@hooks/debounceAsync';
import OrdersService from '@utility/services/order';
import { BrowserUtility } from '@utility/browser-utility';
import { organizationSettingsHook } from '@hooks/organizations';
import { Checkbox, cn, Tooltip } from '@skupreme/shared/ui';
import { UploadShippingLabelPopup } from './UploadShippingLabelPopup';
import { X } from '@phosphor-icons/react';
/* The overlay panel scrolls itself inside the viewport, under the header and
   above the pinned button box, which the bottom padding keeps clear. */
function LabelContainer({
  variant,
  noShadow,
  footerActive,
  isNotManualFP,
  children,
}: {
  variant?: string;
  noShadow?: boolean;
  footerActive?: boolean;
  isNotManualFP?: boolean | number;
  children?: ReactNode;
}) {
  if (variant === 'page') {
    return <div className="sk:mb-6">{children}</div>;
  }
  if (noShadow) {
    return (
      <div
        className={cn(
          'sk:mx-auto sk:my-4 sk:overflow-y-auto',
          footerActive
            ? 'sk:h-[calc(100vh-170px)]'
            : 'sk:h-[calc(100vh-150px)]',
          isNotManualFP ? 'sk:pb-[120px]' : 'sk:pb-[130px]',
        )}
      >
        {children}
      </div>
    );
  }
  return (
    <ShadowBox
      className={cn(
        'sk:mx-auto sk:my-4 sk:overflow-y-auto',
        footerActive ? 'sk:h-[calc(100vh-190px)]' : 'sk:h-[calc(100vh-166px)]',
        isNotManualFP ? 'sk:pb-[140px]' : 'sk:pb-[170px]',
      )}
    >
      {children}
    </ShadowBox>
  );
}
const orgIdToHardcodeSkuvault = [
  '65ef1dc44f229033d7b6176e',
  '67cf4bf95a44d4945d7eee32',
];
/* What a warehouse reports about an order line. Inventory comes back as a
   count, and 999999 stands for a warehouse that never runs out. */
type ProviderItem = Omit<FulfillmentItem, 'available'> & {
  available?: number | boolean;
  fulfilled_status?: string;
};
type ProviderData = {
  _id: string;
  name?: string;
  integration_type?: string;
  metadata?: { default?: boolean };
  items?: ProviderItem[];
};
export function CreateShipmentLabel({
  batchId = '',
  changeListFP = () => {},
  createLabelDone = () => {},
  currentFP = '',
  error = '',
  onValidateAddress = () => {},
  /* True when the current list has another order that can be fulfilled. */
  canFulfillNext = false,
  setQueueAdvance = (_requested: boolean) => {},
  orderId = '',
  noShadow = false,
  showRatesByDefault,
  onClose,
  variant = 'panel',
  /* The shipments already bought, which the page owns and the list heads with. */
  labels = [] as OrderShippingLabel[],
  returnLabels = [] as OrderShippingLabel[],
  labelsLoading = false,
  trackingStatuses = [],
  onViewLabel,
  onRefundLabel,
}) {
  const isPage = variant === 'page';
  const { footerActive, currentOrg } = useAuth();
  const { data, loading, setData } = orderDetailsHook(orderId);
  const [isInternational, setIsInternational] = useState(false);
  const { data: orgSettings } = organizationSettingsHook();
  // @ts-expect-error - TS2339: Property 'print_mode' does not exist on type 'never'.
  const enableAutoPrint = orgSettings?.print_mode?.auto_print;
  const [manualAddressUpdate, setManualAddressUpdate] = useState(false);
  const [isSignature, setIsSignature] = useState(false);
  const resolver = useMemo(
    () =>
      zodResolver(
        createShippingSchema({
          dimensions: true,
          weight: true,
          isInternational,
        }),
      ),
    [isInternational],
  );
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { isValid: formIsValid, isSubmitting, errors },
  } = useForm<ShippingFormValues>({
    mode: 'onChange',
    resolver,
    shouldUnregister: true,
  });
  // @ts-expect-error - TS2339: Property 'watchFormFields' does not exist on type 'never[]'.
  const [
    providerId,
    pounds,
    ounces,
    packageW,
    serviceW,
    length,
    width,
    height,
    formProvider,
    shipping_speed,
  ] = useWatch({
    // @ts-expect-error - TS2339: Property 'watchFormFields' does not exist on type 'never[]'.
    name: watchFormFields,
    control,
  });
  const requestedShipping = data?.requested_shipping ?? null;
  /* The marketplace speed this form last defaulted to, so a form value that
     differs from it can be read as the operator's own choice. */
  const appliedRequestedSpeed = useRef<string | null>(null);
  const [shipments, setShipments] = useState<ShipmentPlan[]>([]);
  const [activeShipmentId, setActiveShipmentId] = useState<string>();
  const planningOrderKey = orderId ? String(orderId) : '';
  const [plannedOrderKey, setPlannedOrderKey] = useState('');
  const activePlan = useMemo(
    () => shipments.find(plan => plan.id === activeShipmentId),
    [shipments, activeShipmentId],
  );
  /* The shipment being bought says where it ships from; the form only edits it.
     Asking the form instead leaves the page with no warehouse whenever a field
     is remounted, and everything priced here needs one to ask about. */
  const shipFromId = (isPage ? activePlan?.providerId : providerId?.id) || '';
  const watchFields = useMemo(
    () => ({
      fulfillment_provider_id: shipFromId,
      pounds,
      ounces,
      package: packageW,
      service: serviceW,
      length,
      width,
      height,
      provider: formProvider,
      shipping_speed,
      signature: isSignature,
    }),
    [
      shipFromId,
      pounds,
      ounces,
      packageW,
      serviceW,
      length,
      width,
      height,
      formProvider,
      shipping_speed,
      isSignature,
    ],
  );
  /* The checkbox writes `options.hazmat`; watching a bare `hazmat` read nothing. */
  const isHazmat = useWatch({ name: 'options.hazmat', control });
  const [orderItems, setOrderItems] = useState([]);
  const isUSPS = formProvider === 'usps';
  const shippingLabelHookParams = useMemo(
    () => ({
      order_speed: shipping_speed,
      // @ts-expect-error - TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'st...
      isJMGroup: orgIdToHardcodeSkuvault.includes(currentOrg),
      signature: isSignature,
    }),
    [shipping_speed, currentOrg, isSignature],
  );
  const {
    data: fulfillmentProviders,
    loading: fpLoading,
    list: fpList,
    providerRates,
    b2bWarehouse,
    error: fpErrorMessage,
  } = shippingLabelFPHook(
    orderId,
    batchId,
    orderItems,
    shippingLabelHookParams,
    /* The page quotes the picked warehouse only; the overlay compares them all. */
    isPage ? { mode: 'selected', providerId: shipFromId } : { mode: 'all' },
  );
  const { data: customOrigins, loading: customOriginLoading } =
    shippingOrderFulfillMetadataHook(enableAutoPrint ? orderId : null);
  const { data: aliasInventory, loading: aliasInventoryLoading } =
    useHighestQuantityAliasInventory(shipFromId, orderItems);
  /* The provider quotes catalog items, which carry no marketplace title, so the
     order line has to supply the name the rest of the page shows. */
  const orderLines = useMemo(() => {
    const lines = new Map<string, OrderContent>();

    (data?.order_contents || []).forEach(line => {
      lines.set(line._id, line);
      if (line.content?.item_id) {
        lines.set(line.content.item_id, line);
      }
    });

    return lines;
  }, [data]);

  const orderLineTitle = useCallback(
    (itemId: string) => orderLines.get(itemId)?.content?.title,
    [orderLines],
  );

  const orderItemsWithAlias = useDebouncedMemo(
    () => {
      if (CommonUtility.isValidArray(orderItems)) {
        const temp = orderItems.map((item: FulfillmentItem) => {
          const aliasInfo = aliasInventory?.find(x => x.item_id === item._id);
          return {
            ...item,
            title: orderLineTitle(item._id),
            alias: aliasInfo?.alias,
            loading: aliasInventoryLoading,
          };
        });
        return temp;
      }
      return [];
    },
    [aliasInventory, orderItems, aliasInventoryLoading, orderLineTitle],
    200,
    [],
  );

  /* The warehouse list, typed at the one place the shipment plan reads it. */
  const providerData: ProviderData[] = fulfillmentProviders;

  const plannerProviders = useMemo<PlannerProvider[]>(
    () =>
      providerData.map(provider => ({
        id: provider._id,
        availableItemIds: (provider.items || [])
          .filter(
            item => item.fulfilled_status !== 'FULFILLED' && item.available,
          )
          .map(item => item._id),
      })),
    [providerData],
  );

  /*
   * A shipment that has gone out is shown by the label or the tracking number
   * it produced. Those name the lines they carried, which is how a line that
   * has left is told from one that only looks like it has.
   */
  const dispatchedItemIds = useMemo(() => {
    const ids = new Set<string>();

    (data?.shipping_details || []).forEach(shipment => {
      if (!shipment.tracking_number || shipment.tracking_number === 'TBD') {
        return;
      }
      (shipment.items || []).forEach(item => {
        ids.add(item._id);
      });
    });

    (labels || [])
      .filter(label => !label.refunded)
      .forEach(label => {
        (label.items || []).forEach(item => {
          if (item.item_id) {
            ids.add(item.item_id);
          }
          if (item.bundle_item_id) {
            ids.add(item.bundle_item_id);
          }
        });
      });

    return ids;
  }, [data, labels]);

  /*
   * Every line still owed. The order is asked as well as the warehouses: a line
   * no warehouse holds stock for is left out of their answer altogether, and
   * taking their word for it drops the line from the page along with its stock.
   * Its own state can lag what it has shipped, so a line already on its way is
   * left to the warehouses, which count the quantity that is left.
   */
  const pendingItemIds = useMemo(() => {
    const ids: string[] = [];
    const owed = (itemId?: string) => {
      if (itemId && !ids.includes(itemId)) {
        ids.push(itemId);
      }
    };

    providerData.forEach(provider =>
      (provider.items || []).forEach(item => {
        if (item.fulfilled_status !== 'FULFILLED') {
          owed(item._id);
        }
      }),
    );

    (data?.order_contents || []).forEach(line => {
      if (
        line.content?.item_status === OrderStatus.PendingShipment &&
        !dispatchedItemIds.has(line._id)
      ) {
        owed(line._id);
      }
    });

    return ids;
  }, [providerData, data, dispatchedItemIds]);

  const itemsById = useMemo(() => {
    const map = new Map<string, ProviderItem>();
    providerData.forEach(provider =>
      (provider.items || []).forEach(item => {
        if (!map.has(item._id)) {
          map.set(item._id, item);
        }
      }),
    );
    return map;
  }, [providerData]);

  /* Where the order ships from unless it has to be split. */
  const defaultProviderId = useMemo(() => {
    if (providerData.length === 0) {
      return undefined;
    }
    const skuVault = orgIdToHardcodeSkuvault.includes(String(currentOrg))
      ? providerData.find(
          provider =>
            provider.integration_type === StorageIntegrationTypes.skuVault,
        )
      : undefined;

    return (
      skuVault?._id ||
      providerData.find(provider => provider._id === currentFP)?._id ||
      providerData.find(provider => provider.metadata?.default)?._id ||
      providerData[0]?._id
    );
  }, [providerData, currentOrg, currentFP]);

  /* Planned once the warehouses have answered, empty answer included: a line
     none of them can ship is still owed, and it is listed as unstocked. The
     card that stood in for a purchase in flight steps aside once the shipment
     it made has a label of its own to be listed under. */
  useEffect(() => {
    if (!isPage || fpLoading) {
      return;
    }
    setShipments(current =>
      reconcilePlans(
        current.filter(
          plan =>
            plan.status !== 'shipped' ||
            !plan.itemIds.every(itemId => dispatchedItemIds.has(itemId)),
        ),
        {
          providers: plannerProviders,
          pendingItemIds,
          preferredProviderId: defaultProviderId,
        },
      ),
    );
    setPlannedOrderKey(planningOrderKey);
  }, [
    isPage,
    fpLoading,
    plannerProviders,
    pendingItemIds,
    defaultProviderId,
    dispatchedItemIds,
    planningOrderKey,
  ]);

  /* The form follows the open shipment: once one ships, the next one takes over. */
  useEffect(() => {
    if (!isPage) {
      return;
    }
    const next =
      activePlan && isOpen(activePlan) ? activePlan : nextOpenPlan(shipments);
    if (next?.id !== activeShipmentId) {
      setActiveShipmentId(next?.id);
    }
  }, [isPage, shipments, activePlan, activeShipmentId]);

  const enrichedItems = useMemo(
    () => new Map((orderItemsWithAlias || []).map(item => [item._id, item])),
    [orderItemsWithAlias],
  );

  const shipmentItem = useCallback(
    (itemId: string): FulfillmentItem => {
      const enriched = enrichedItems.get(itemId);
      if (enriched) {
        return enriched;
      }
      const raw = itemsById.get(itemId);
      const line = orderLines.get(itemId);
      return {
        p_title: line?.p_title,
        p_sku: line?.p_sku,
        upc: line?.upc,
        p_images: line?.p_images,
        qty: line?.content?.qty,
        ...raw,
        _id: itemId,
        available: !!raw?.available,
        title: raw?.title || line?.content?.title,
      };
    },
    [enrichedItems, itemsById, orderLines],
  );

  const isItemAvailableAt = useCallback(
    (itemId: string, targetProviderId: string) =>
      !!plannerProviders
        .find(provider => provider.id === targetProviderId)
        ?.availableItemIds.includes(itemId),
    [plannerProviders],
  );

  /* The warehouses have said what they hold, so their answer can be read. Until
     then a line's stock is unknown, which is not the same as having none. */
  const stockKnown = plannerProviders.length > 0;

  /* Stock is read against the warehouse this shipment ships from, so moving a
     line into a warehouse that does not hold it says so on the line. */
  const shipmentViews = useMemo<ShipmentView[]>(
    () =>
      shipments.map(plan => ({
        id: plan.id,
        providerId: plan.providerId,
        providerName: providerData.find(
          provider => provider._id === plan.providerId,
        )?.name,
        status: plan.status,
        error: plan.error,
        items: plan.itemIds.map(itemId => ({
          ...shipmentItem(itemId),
          available: stockKnown
            ? isItemAvailableAt(itemId, plan.providerId)
            : undefined,
        })),
      })),
    [shipments, providerData, shipmentItem, isItemAvailableAt, stockKnown],
  );

  /*
   * Lines no warehouse has stock for: no shipment can take them, and they are
   * listed on their own so an order is never half-shipped in silence. Only the
   * warehouses can say a line has nowhere to ship from, so a line is asked of
   * them rather than read off a plan that is a render behind them — otherwise
   * every line looks out of stock until the plan catches up.
   */
  const unstockedItems = useMemo(() => {
    if (!stockKnown && fpLoading) {
      return [];
    }

    const planned = shipments.flatMap(plan => plan.itemIds);
    const stockedSomewhere = (itemId: string) =>
      plannerProviders.some(provider =>
        provider.availableItemIds.includes(itemId),
      );

    return pendingItemIds
      .filter(itemId => !planned.includes(itemId) && !stockedSomewhere(itemId))
      .map(itemId => ({
        ...shipmentItem(itemId),
        available: false,
        /* Held in a B2B warehouse is not the same as gone, and reads differently. */
        unavailableReason: b2bWarehouse?.item_ids.includes(itemId)
          ? b2bWarehouse.reason
          : undefined,
      }));
  }, [
    fpLoading,
    stockKnown,
    plannerProviders,
    pendingItemIds,
    shipments,
    shipmentItem,
    b2bWarehouse,
  ]);

  /* A split is a second parcel, not a second warehouse: the line stays where it
     was shipping from, and only moves house if that one cannot stock it. */
  const providerForItem = useCallback(
    (itemId: string) => {
      const current = findPlanOfItem(shipments, itemId)?.providerId;
      if (current && isItemAvailableAt(itemId, current)) {
        return current;
      }

      return (
        plannerProviders.find(provider =>
          provider.availableItemIds.includes(itemId),
        )?.id ||
        defaultProviderId ||
        ''
      );
    },
    [shipments, isItemAvailableAt, plannerProviders, defaultProviderId],
  );

  const moveShipmentItem = (itemId: string, targetShipmentId: string) =>
    setShipments(plans => moveItem(plans, itemId, targetShipmentId));

  /* Nowhere to split it to while no warehouse is known. */
  const splitShipmentItem = (itemId: string) => {
    const providerId = providerForItem(itemId);
    if (providerId) {
      setShipments(plans => splitItem(plans, itemId, providerId));
    }
  };

  const mergeShipments = () => {
    if (activeShipmentId) {
      setShipments(plans => mergeInto(plans, activeShipmentId));
    }
  };

  /* Changing the warehouse changes the shipment; the form follows whichever
     shipment it is bound to, and a queued one just remembers the choice. */
  const shipmentProviderPicked = (shipmentId: string, providerId: string) =>
    setShipments(plans => setPlanProvider(plans, shipmentId, providerId));

  const shipmentProviderChanged = item => {
    const id = item?.value?.id;
    if (id && activeShipmentId) {
      shipmentProviderPicked(activeShipmentId, id);
    }
  };

  const markActiveShipment = (status: ShipmentStatus, error?: string) => {
    if (isPage && activeShipmentId) {
      setShipments(plans =>
        setPlanStatus(plans, activeShipmentId, status, error),
      );
    }
  };

  const packageQuote = shipFromId ? providerRates?.[shipFromId] : undefined;

  /*
   * A re-quote replaces the quote with a bare loading flag, which would empty
   * the list and blank the card. The last boxes we were told about stand in
   * until the new ones arrive.
   */
  const quoted = useMemo(() => quotedBoxes(packageQuote), [packageQuote]);
  const fromAttributes = useMemo(() => {
    const provider = (fulfillmentProviders || []).find(
      item => item._id === shipFromId,
    );
    return attributeBoxes(provider?.packages);
  }, [fulfillmentProviders, shipFromId]);
  const [knownBoxes, setKnownBoxes] = useState<PackageBox[]>([]);
  useEffect(() => {
    if (quoted.length) {
      setKnownBoxes(quoted);
    }
  }, [quoted]);
  const packageBoxes = quoted.length
    ? quoted
    : knownBoxes.length
      ? knownBoxes
      : fromAttributes;

  useEffect(() => {
    setKnownBoxes([]);
  }, [shipFromId]);

  const currentProvider = useMemo(
    () => fpList?.find(x => x.value === shipFromId)?.item,
    [shipFromId, fpList],
  );

  /* Undefined until a warehouse is known: neither shipping form is right to guess. */
  const isNotManualFP = currentProvider
    ? currentProvider.integration_type !== 'manual'
    : undefined;

  useEffect(() => {
    setIsInternational(currentProvider?.is_international || false);
  }, [currentProvider]);

  const [isShipManual, setShipManual] = useState(false);
  const { data: metadata, loading: metadataLoading } =
    shippingLabelMetadataHook(isInternational, isShipManual);
  const [fulfilledLabels, setFulfilledLabels] = useState([]);
  const [saving, setSaving] = useState(false);
  const fulfillmentInFlight = useRef(false);
  const [generatedLabel, setGenerateLabel] = useState(null);
  const labelSubmissionActive = useRef(false);
  const labelSubmissionMounted = useRef(true);
  useEffect(() => {
    labelSubmissionMounted.current = true;
    return () => {
      labelSubmissionMounted.current = false;
    };
  }, []);
  const [showPrintLabel, setShowPrintLabel] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [uploadLabelPopup, setUploadLabelPopup] = useState(false);
  const [addBalancePopup, setAddBalancePopup] = useState(false);
  const [addCreditCardPopup, setAddCreditCardPopup] = useState(false);
  const [formAddressOverride, setFormAddressOverride] = useState(null);
  const [provider, setProvider] = useState(null);
  const [service, setService] = useState(null);

  useMemo(() => {
    // @ts-expect-error - TS2339: Property 'signature' does not exist on type 'never'.
    setIsSignature(() => orderItems?.some(item => item.signature || false));
  }, [orderItems]);

  /* A warehouse ships at the speeds it supports; these are the fallback. */
  const shippingSpeedData = useMemo(() => {
    const speeds = CommonUtility.isValidArray(currentProvider?.shipping_speed)
      ? currentProvider.shipping_speed
      : ['Standard', 'Expedited', 'Priority'];

    return speeds.map(item => ({
      value: item,
      text: CommonUtility.toTitleCase(item),
    }));
  }, [currentProvider]);

  /* The order detail can land after a warehouse has already filled the form with
     its own first speed, and the marketplace instruction still has to win when it
     does. Once applied, the ref keeps this from fighting the operator. */
  useEffect(() => {
    if (appliedRequestedSpeed.current || !requestedShipping?.speed) return;

    const next = resolveDefaultShippingSpeed({
      requestedSpeed: requestedShipping.speed,
      warehouseSpeeds: currentProvider?.shipping_speed,
      currentSpeed: shipping_speed,
      appliedRequestedSpeed: null,
    });
    if (!next.fromMarketplace) return;

    appliedRequestedSpeed.current = next.speed;
    if (next.speed !== shipping_speed) setValue('shipping_speed', next.speed);
  }, [requestedShipping, currentProvider, shipping_speed, setValue]);

  const orderAddress = useMemo(() => {
    if (!data?.customer_details) return null;
    return {
      address_one: data.customer_details.address_line_one,
      address_two: data.customer_details.address_line_two || '',
      city: data.customer_details.city,
      state: data.customer_details.state,
      postal_code: data.customer_details.zip,
      country: data.customer_details.country,
      name: data.customer_details.name,
      email: data.customer_details.e_mail,
      phone: data.customer_details.phone,
      override: CommonUtility.isNotNull(formAddressOverride)
        ? formAddressOverride
        : data.customer_details.override,
    };
  }, [data]);

  const customTotal = useMemo(
    () => CommonUtility.getCustomItemsTotal(data?.custom_items),
    [data],
  );

  const {
    valid: isValidAddress,
    loading: addressValidationLoading,
    data: addressValidationData,
    error: addressValidationError,
  } = addressValidationHook(orderAddress, orderId);
  const isRFCRequired = useMemo(
    () =>
      data?.customer_details?.country !==
        BrowserUtility.get(CommonConstant.currentRegion) &&
      ['MX'].includes(data?.customer_details?.country ?? ''),
    [data],
  );

  const providers = useMemo(
    () =>
      // @ts-expect-error - TS2339: Property 'map' does not exist on type 'true | never[]'.
      (metadata || []).map(item => ({
        value: item.provider.value,
        text: item.provider.name,
      })),
    [metadata],
  );

  // Mapped Services & Content Types
  const { services, contentTypes } = useMemo(() => {
    if (!provider) {
      return { services: [] };
    }

    // Options To Be Used In Select Field For Services Field
    const services = (metadata || [])
      // @ts-expect-error - TS2339: Property 'find' does not exist on type 'true | never[]'.
      ?.find(x => x.provider.value === provider)
      ?.service?.filter(x => !!x.rate)
      .map(item => ({
        ...item,
        value: item.value,
        text: item.name,
      }));

    // Options To Be Used In Select Field For Content Type Field
    const contentTypes = (metadata || [])
      // @ts-expect-error - TS2339: Property 'find' does not exist on type 'true | never[]'.
      .find(x => x.provider.value === provider)
      ?.contentTypes?.map(item => ({
        ...item,
        value: item.value,
        text: item.name,
      }));

    return { services, contentTypes };
  }, [provider, metadata]);

  const packages = useMemo(() => {
    if ((services || []).length === 0 || !service) {
      return [];
    }

    // Options To Be Used In Select Field For Services Field
    const packages = services
      .find(x => x.value === service)
      ?.package.map(item => ({
        ...item,
        value: item.value,
        text: item.name,
      }));

    // Reset Package Field Value
    if (packages?.length === 1) {
      setValue('package', packages[0]?.value || '');
    }
    return packages || [];
  }, [service, services]);

  const show = useMemo(() => {
    if (watchFields.package) {
      const p = packages.find(x => x.value === watchFields.package);
      return {
        dimensions: !p?.hide_dimensions,
        weight: !p?.hide_weights,
      };
    }
    return {
      dimensions: true,
      weight: true,
    };
  }, [watchFields.package, packages]);

  const providerChanged = item => {
    setProvider(item.value);
    setValue('service', '');
    setValue('package', '');
  };

  const serviceChanged = item => {
    setService(item.value);
    // setValue('package','');
  };

  /* A fulfill queue moves from order to order without leaving the page, so
     every shipment planned for the one left behind goes with it. */
  useEffect(() => {
    const temp = {};
    watchFormFields.forEach(item => (temp[item] = null));
    reset(temp);
    appliedRequestedSpeed.current = null;
    setOrderItems([]);
    setFulfilledLabels([]);
    setShipments([]);
    setActiveShipmentId(undefined);
  }, [orderId]);

  /* The print settings and their origins arrive after the form is up. They own
     two fields, so they fill those two instead of resetting the whole form,
     which would drop the warehouse and the lines already chosen. */
  useEffect(() => {
    setValue('custom_origin', !!enableAutoPrint);
    setValue('origin_name', customOrigins?.find(x => x.primary)?.name || '');
  }, [orderId, enableAutoPrint, customOrigins, setValue]);

  /* On the page the shipment plan picks the warehouse, one card at a time. */
  useDebouncedEffect(
    () => {
      if (isPage) {
        return;
      }
      // @ts-expect-error - TS2339: Property 'length' does not exist on type 'boolean'.
      if (metadata.length > 0 && (fulfillmentProviders || []).length > 0) {
        let defaultFP;
        // @ts-expect-error - TS2345: Argument of type 'string | undefined' is not assignable to parameter of type 'st...
        if (orgIdToHardcodeSkuvault.includes(currentOrg)) {
          defaultFP = fulfillmentProviders.find(
            x => x.integration_type === StorageIntegrationTypes.skuVault,
          );
        }
        if (!defaultFP && currentFP) {
          defaultFP = fulfillmentProviders.find(x => x._id === currentFP);
        }
        if (!defaultFP && !providerId) {
          defaultFP = fulfillmentProviders.find(x => x.metadata?.default);
        }
        if (defaultFP) {
          fulfillmentProviderChanged({ value: { id: defaultFP._id } });
        }
      }
    },
    [metadata, fulfillmentProviders, currentFP],
    300,
  );

  // TODO: zeroing the ounces drops the remainder (20 oz becomes 1 lb 0 oz). Should carry oz % 16.
  // Same copy exists in six label/rate forms; extract one helper.
  useEffect(() => {
    if (ounces > 15) {
      setValue('ounces', 0);
      setValue('pounds', Number(getValues('pounds') || 0) + 1);
    }
  }, [ounces]);

  const {
    rate,
    time,
    loading: rateLoading,
    error: rateError,
    rateNegotiate,
    transactionId,
    ddp,
  } = rateHook(watchFields, orderId, show);

  const hasMissingHTSCode = useMemo(() => {
    if (!isInternational) return false;

    return orderItems.some(item => {
      // @ts-expect-error - TS2339: Property 'hts_code' does not exist on type 'never'.
      if (item.checked) {
        // @ts-expect-error - TS2339: Property 'hts_code' does not exist on type 'never'.
        return !item.hts_code;
      }
      return false;
    });
  }, [isInternational, orderItems]);

  const activeShipmentHasStock =
    !isPage ||
    fpLoading ||
    hasAvailableShipmentItems(activePlan, plannerProviders);

  const disabledCreateLabel = useMemo(
    () =>
      rateLoading ||
      !!rateError ||
      !isValidAddress ||
      !formIsValid ||
      isSubmitting ||
      !CommonUtility.isNotEmpty(rate) ||
      hasMissingHTSCode,
    [
      rateLoading,
      rateError,
      isValidAddress,
      formIsValid,
      isSubmitting,
      rate,
      hasMissingHTSCode,
    ],
  );

  const invalidAddress = useMemo(
    () => !isValidAddress && !addressValidationLoading,
    [isValidAddress, addressValidationLoading],
  );

  const onSubmit = async (data, testMode) => {
    if (labelSubmissionActive.current) return;
    labelSubmissionActive.current = true;
    try {
      const checkedItems = orderItems
        // @ts-expect-error - TS2339: Property 'checked' does not exist on type 'never'.
        .filter(x => x.checked)
        .map(x => ({
          // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
          item_id: x._id,
          // @ts-expect-error - TS2339: Property 'newQty' does not exist on type 'never'.
          qty: x.newQty || (x.qty || 0) - (x.fulfilledQty || 0),
          // @ts-expect-error - TS2339: Property 'sister_item' does not exist on type 'never'.
          sister_item_id: x.sister_item?._id,
        }));
      if (checkedItems.length === 0) {
        ToastMessage.error('Select items to create shipment label');
        return;
      }
      // @ts-expect-error - TS2345: Argument of type '"Generating Label"' is not assignable to parameter of type 'Se...
      setSaving('Generating Label');
      markActiveShipment('busy');
      const fpData = fulfillmentProviders.find(
        x => x._id === data.fulfillment_provider_id,
      );
      const reqData = {
        ...data,
        pounds: Number.parseFloat(data?.pounds || 0),
        ounces: Number.parseFloat(data?.ounces || 0),
        order_id: orderId,
        girth: 0,
        mode: testMode ? 'test' : 'production',
        items: checkedItems,
        custom_ref_1: fpData?.metadata?.request_object?.custom_ref_1,
        transaction_id: transactionId,
        fulfillment_provider_id: data?.fulfillment_provider_id?.id,
        ...(data?.fulfillment_provider_id?.credentials_id
          ? { credentials_id: data.fulfillment_provider_id.credentials_id }
          : {}),
      };
      if (batchId && testMode) reqData.batch_test = true;
      if (batchId) {
        reqData.poll = true;
        reqData.batch_id = batchId;
      }
      if (reqData.package === 'letter') {
        delete reqData.length;
        delete reqData.width;
        delete reqData.height;
      }
      delete reqData.saveDefault;

      if (!enableAutoPrint || !reqData.custom_origin) {
        delete reqData.origin_name;
      }

      delete reqData.custom_origin;

      delete reqData.options.insurance;
      const accepted = await ShippingLabelService.add(reqData);
      const result = await settlePendingWalletLabel(
        accepted,
        () => ShippingLabelService.operation(accepted.data.request_id),
        () => {
          // @ts-expect-error - legacy saving state is inferred as boolean.
          setSaving('Waiting for wallet funds');
        },
        () => labelSubmissionMounted.current,
      );
      if (!result) return;
      markActiveShipment('shipped');
      if (batchId) {
        // @ts-expect-error - TS2554: Expected 0 arguments, but got 2.
        createLabelDone(true, orderId);
      } else {
        setGenerateLabel({
          testMode,
          ...result.data,
        });
        setShowPrintLabel(true);
      }
    } catch (error) {
      markActiveShipment(
        'failed',
        error?.error || ErrorMessageConstant.defaultAPIError,
      );
      if (error.code === 'low_balance') {
        setAddBalancePopup(true);
      } else if (error.code === 'payment_method_required') {
        setAddCreditCardPopup(true);
      } else {
        ToastMessage.error(
          error?.error || ErrorMessageConstant.defaultAPIError,
        );
      }
    } finally {
      labelSubmissionActive.current = false;
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<b...
      setSaving(null);
    }
  };

  /* `itemIds` narrows the warehouse's lines to the ones a single shipment
     carries; without it the whole warehouse ships, as the overlay does. */
  const fulfillmentProviderChanged = (item, itemIds?: string[]) => {
    const fp = fulfillmentProviders.find(x => x._id === item?.value?.id) || {};
    const temp = fp?.items
      ?.filter(
        x =>
          x.fulfilled_status !== 'FULFILLED' &&
          (!itemIds || itemIds.includes(x._id)),
      )
      .map(t => {
        const z = { ...t };
        z.checked = t.available;
        return z;
      });
    setOrderItems(temp);

    const tempFulfilled = fp.items?.filter(
      x => x.fulfilled_status === 'FULFILLED',
    );
    setFulfilledLabels(tempFulfilled);

    /* The marketplace's own instruction outranks the warehouse's first speed,
       which is what the form used to fall back to. Anything the operator picked
       on top of it still stands. */
    const defaultSpeed = resolveDefaultShippingSpeed({
      requestedSpeed: requestedShipping?.speed,
      warehouseSpeeds: fp.shipping_speed,
      currentSpeed: shipping_speed,
      appliedRequestedSpeed: appliedRequestedSpeed.current,
    });
    appliedRequestedSpeed.current = defaultSpeed.fromMarketplace
      ? defaultSpeed.speed
      : appliedRequestedSpeed.current;

    const formData = {
      ...(fp?.metadata?.request_object || {}),
      fulfillment_provider_id: {
        id: fp._id,
        service: item?.value?.service || null,
        provider: item?.value?.provider || null,
        packageValue: item?.value?.packageValue || null,
        credentials_id: item?.value?.credentials_id || null,
      },
      shipping_speed: defaultSpeed.speed,
    };
    const nextValues = {
      ...formData,
      custom_origin: !!enableAutoPrint,
      origin_name: customOrigins?.find(x => x.primary)?.name || '',
    };
    reset(nextValues, { keepFieldsRef: true });
    /*
     * The form unregisters what leaves the screen, and under that a reset only
     * refills the defaults: the fields keep no value of their own until they
     * register again. Writing the values in publishes them to everything
     * watching the form, which is what decides the warehouse to quote.
     */
    Object.entries(nextValues).forEach(([name, value]) =>
      // @ts-expect-error - TS2345: the legacy warehouse payload is untyped.
      setValue(name, value),
    );
    setProvider(formData.provider);
    setService(formData.service);
    if (changeListFP) {
      setTimeout(() => {
        // @ts-expect-error - TS2554: Expected 0 arguments, but got 1.
        changeListFP({
          _id: fp._id,
          name: fp.name,
          integration_type: fp.integration_type,
        });
      });
    }
  };

  /*
   * The form holds one shipment at a time, so opening one loads it: its
   * warehouse, its lines, its defaults. Keyed on what the shipment is rather
   * than on when it changed, since reloading it would discard a filled-in form.
   */
  const activation =
    isPage && activePlan && isOpen(activePlan)
      ? `${activePlan.id}:${activePlan.providerId}:${activePlan.itemIds.join(',')}`
      : '';
  const loadedShipment = useRef('');
  /* The plan can name a warehouse the list has not delivered yet, and loading a
     warehouse we know nothing about would clear the form and ask again. */
  const isPlanProviderKnown =
    !!activePlan &&
    providerData.some(provider => provider._id === activePlan.providerId);

  useEffect(() => {
    /* Loading the same shipment twice would discard a filled-in form. */
    if (
      !activation ||
      !activePlan ||
      !isPlanProviderKnown ||
      loadedShipment.current === activation
    ) {
      return;
    }
    loadedShipment.current = activation;
    fulfillmentProviderChanged(
      { value: { id: activePlan.providerId } },
      activePlan.itemIds,
    );
  }, [activation, activePlan, isPlanProviderKnown]);

  /* Only the carrier tuple changes here: resetting the form would re-quote the
     warehouse and put every item back to its default. */
  const rateSelected = selection => {
    setValue(
      'fulfillment_provider_id',
      {
        id: selection?.id || getValues('fulfillment_provider_id')?.id,
        service: selection?.service || null,
        provider: selection?.provider || null,
        packageValue: selection?.packageValue || null,
        credentials_id: selection?.credentials_id || null,
      },
      { shouldDirty: true },
    );
  };

  const toggleItems = (item, isChecked) => {
    const temp = orderItems.map(t => {
      // @ts-expect-error - TS2698: Spread types may only be created from object types.
      const z = { ...t };
      if (item._id === z._id) {
        z.checked = isChecked;
      }
      return z;
    });
    // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
    setOrderItems(temp);
  };

  /* The queue is told the order is done, and a shipment is not the order: with
     the lines split across shipments, only the one that leaves nothing owed
     finishes it. */
  const shipsWholeOrder = (shippedItemIds: string[]) =>
    pendingItemIds.every(itemId => shippedItemIds.includes(itemId));

  const qtyChanged = (item, quantity) => {
    const temp = orderItems.map(t => {
      /* @ts-expect-error - TS2698: Spread types may only be created from object types. */
      const z = { ...t };
      if (item._id === z._id) {
        z.newQty = quantity;
      }
      return z;
    });
    // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
    setOrderItems(temp);
  };

  const autoFulfillerClicked = async (
    testMode = false,
    labelAttachment = null,
  ) => {
    if (fulfillmentInFlight.current) {
      return false;
    }
    fulfillmentInFlight.current = true;

    try {
      const checkedItems = orderItems
        // @ts-expect-error - TS2339: Property 'checked' does not exist on type 'never'.
        .filter(x => x.checked)
        .map(x => ({
          // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
          item_id: x._id,
          // @ts-expect-error - TS2339: Property 'sister_item' does not exist on type 'never'.
          sister_item_id: x.sister_item?._id,
          // @ts-expect-error - TS2339: Property 'newQty' does not exist on type 'never'.
          qty: x.newQty || (x.qty || 0) - (x.fulfilledQty || 0),
        }));

      const selectedFP = getValues('fulfillment_provider_id');
      const fulfillment_provider_id = selectedFP.id;
      const fp = fulfillmentProviders.find(
        x => x._id === fulfillment_provider_id,
      );

      if (checkedItems.length === 0) {
        ToastMessage.error('Select items to create shipment label');
        return;
      }
      // @ts-expect-error - TS2345: Argument of type '"Fulfilling"' is not assignable to parameter of type 'SetState...
      setSaving('Fulfilling');
      markActiveShipment('busy');
      const reqData = {
        order_id: orderId,
        fulfillment_provider_id,
        shipping_speed: getValues('shipping_speed'),
        items: checkedItems,
        integration_type: fp.integration_type,
        fba: fp.fba,
        deliverr: fp.deliverr,
        scanner: !!enableAutoPrint,
      } as any;

      if (enableAutoPrint && getValues('custom_origin')) {
        reqData.origin_name = getValues('origin_name');
      }

      if (
        CommonUtility.isNotNull(selectedFP.provider) &&
        CommonUtility.isNotNull(selectedFP.packageValue) &&
        CommonUtility.isNotNull(selectedFP.service)
      ) {
        reqData.provider = selectedFP.provider;
        reqData.package = selectedFP.packageValue;
        reqData.service = selectedFP.service;
      }
      if (selectedFP.credentials_id) {
        reqData.credentials_id = selectedFP.credentials_id;
      }

      if (labelAttachment) {
        reqData.labelAttachment = labelAttachment;
      }

      if (testMode) {
        reqData.mode = 'test';
      }
      reqData.options = {};
      reqData.options.signature = isSignature;
      reqData.options.tax_code = getValues('options.tax_code');
      const res = await ShippingLabelService.orderFulfill(reqData);
      let passBack = null;
      if (res?.data && enableAutoPrint) {
        passBack = res?.data;
      }

      closeUploadLabelPopup();

      if (testMode) {
        // @ts-expect-error - TS2554: Expected 0 arguments, but got 3.
        createLabelDone(false, orderId, passBack);
      } else {
        createLabelDone(
          // @ts-expect-error - TS2554: Expected 0 arguments, but got 3.
          shipsWholeOrder(checkedItems.map(item => item.item_id)),
          orderId,
          passBack,
        );
      }

      ToastMessage.success('Order fulfillment initiated successfully');
      return true;
    } catch (error) {
      markActiveShipment(
        'failed',
        error?.error || error?.message || ErrorMessageConstant.defaultAPIError,
      );
      ToastMessage.apiError(error);
    } finally {
      fulfillmentInFlight.current = false;
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<b...
      setSaving(null);
    }
    return false;
  };

  /* The intent has to be set before fulfilling, because the queue moves on from
     inside `createLabelDone`. A failed request never gets there, so it undoes it. */
  const fulfillAndNextClicked = canFulfillNext
    ? async () => {
        setQueueAdvance(true);
        const fulfilled = await autoFulfillerClicked();
        if (!fulfilled) {
          setQueueAdvance(false);
        }
      }
    : undefined;

  const closeAddressValidationPopup = addressResponse => {
    setManualAddressUpdate(() => false);
    if (addressResponse) {
      if (data) {
        setData({
          ...data,
          customer_details: {
            ...data.customer_details,
            ...addressResponse,
          },
        });
      }

      // @ts-expect-error - TS2554: Expected 0 arguments, but got 1.
      onValidateAddress?.(addressResponse);
    }
    setShowAddressPopup(false);
  };

  const balancePopupClose = value => {
    setAddBalancePopup(false);
    if (value) {
      handleSubmit(onSubmit)();
    }
  };

  const creditCardPopupClose = value => {
    setAddCreditCardPopup(false);
    if (value) {
      setAddBalancePopup(true);
    }
  };

  const printLabelClose = () => {
    setShowPrintLabel(false);
    // @ts-expect-error - TS2339: Property 'testMode' does not exist on type 'never'.
    if (generatedLabel?.testMode) {
      // @ts-expect-error - TS2554: Expected 0 arguments, but got 2.
      createLabelDone(false, orderId);
    } else {
      createLabelDone(
        // @ts-expect-error - TS2554: Expected 0 arguments, but got 2.
        shipsWholeOrder(
          // @ts-expect-error - TS2339: the legacy order line payload is untyped.
          orderItems.filter(item => item.checked).map(item => item._id),
        ),
        orderId,
      );
    }
  };

  // DISABLE MANUAL SHIP > IF Invalid Address
  const isNonManualShippingDisabled =
    !isValidAddress || addressValidationLoading;
  const manualShippingDisabledReason = addressValidationLoading
    ? 'Address validation is in progress'
    : !isValidAddress
      ? 'Validate the shipping address first'
      : '';

  const manualShipToggle = isPage ? (
    <Tooltip
      content={manualShippingDisabledReason}
      isDisabled={!manualShippingDisabledReason}
    >
      <Checkbox
        isSelected={isShipManual}
        isDisabled={isNonManualShippingDisabled && !isShipManual}
        onChange={setShipManual}
      >
        Ship manually
      </Checkbox>
    </Tooltip>
  ) : undefined;

  /* SP-API warehouses report their own tracking, so they take none from us. */
  const manualShipProviderOptions = useMemo(
    () =>
      (fpList || [])
        .filter(option => option.item?.integration_type !== 'spapi')
        .map(option => ({
          value: option.value,
          text: option.text,
          item: option.item,
        })),
    [fpList],
  );

  const { handleSubmit: manualShipSubmit, loading: manualShipSaving } =
    useShippingLabelsManualShipOrder(orderId);

  /* Called directly, not from an effect: by the time the ship request answers,
     the pending fulfillment request has usually already replaced this form, so
     an effect here would never run. The parent's callback still works after
     the unmount: it refreshes the sidebar counts and the labels and advances
     the fulfil queue. */
  const manualShip = async payload => {
    if (await manualShipSubmit(payload)) {
      // @ts-expect-error - TS2554: Expected 0 arguments, but got 2.
      createLabelDone(true, orderId);
    }
  };

  const uploadShippingLabel = () => {
    setUploadLabelPopup(true);
  };

  const closeUploadLabelPopup = () => {
    setUploadLabelPopup(false);
  };

  const forceGeneratePublicDimension = async () => {
    try {
      // @ts-expect-error - TS2339: Property 'checked' does not exist on type 'never'.
      const checkedItems = orderItems.filter(x => x.checked);
      if (!CommonUtility.isValidArray(checkedItems)) {
        ToastMessage.error('Please select some items');
        return;
      }
      // @ts-expect-error - TS2345: Argument of type '"Generating public link"' is not assignable to parameter of ty...
      setSaving('Generating public link');
      const payload = {
        order_id: orderId,
        fulfillment_provider_id: shipFromId,
        items: [],
        order_speed: getValues('shipping_speed'),
        mode: CommonUtility.isProductionEnv() ? 'production' : 'test',
      };

      // @ts-expect-error - TS2322: Type '{ item_id: any; qty: any; }[]' is not assignable to type 'never[]'.
      payload.items = checkedItems.map(item => ({
        // @ts-expect-error - TS2339: Property '_id' does not exist on type 'never'.
        item_id: item._id,
        // @ts-expect-error - TS2339: Property 'qty' does not exist on type 'never'.
        qty: item.qty,
      }));
      const res = await OrdersService.generateOrderPublicUrl(payload);
      if (res?.data?.url) {
        window.open(res?.data?.url, '_blank');
      } else {
        ToastMessage.warning(
          res?.data?.message || ErrorMessageConstant.defaultAPIError,
        );
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'SetStateAction<b...
      setSaving(null);
    }
  };

  return (
    <>
      {!isPage && (
        <div className="sk:mb-2 sk:flex sk:justify-end sk:p-2">
          <X size={20} onClick={onClose} className="sk:cursor-pointer" />
        </div>
      )}
      {!isPage ? (
        <PageHeaderText className="sk:flex sk:items-center sk:justify-between sk:gap-2">
          <span>Create Shipping Label</span>
          <div className="sk:flex sk:items-center sk:gap-2 sk:text-xs">
            <StyledToggle
              checked={isShipManual}
              onChange={() => setShipManual(prev => !prev)}
              id="manually-ship-order"
            />
            <label
              className={cn(
                'sk:mb-0 sk:cursor-pointer sk:select-none',
                isNonManualShippingDisabled && 'sk:cursor-not-allowed',
              )}
              htmlFor="manually-ship-order"
            >
              Manually Ship Order
            </label>
          </div>
        </PageHeaderText>
      ) : null}

      {isShipManual && (
        <LabelContainer footerActive={footerActive} noShadow variant={variant}>
          <ManualShipCard
            items={CommonUtility.getValidArray(data?.order_contents)}
            orderItems={orderItemsWithAlias || []}
            providerOptions={manualShipProviderOptions}
            providersLoading={fpLoading}
            carriers={providers}
            carriersLoading={metadataLoading}
            defaultProviderId={shipFromId}
            isSaving={manualShipSaving}
            onSubmit={manualShip}
            onCancel={() => setShipManual(false)}
            modeControl={manualShipToggle}
          />
        </LabelContainer>
      )}

      {!isShipManual && (
        <form noValidate onSubmit={handleSubmit(data => onSubmit(data, false))}>
          <LabelContainer
            footerActive={footerActive}
            isNotManualFP={isNotManualFP ? 1 : 0}
            noShadow={noShadow}
            variant={variant}
          >
            {/* @ts-expect-error - TS2322: Type 'true | "Loading"' is not assignable to type 'string | undefined'. */}
            {(saving || loading) && <LoaderBar content={saving || 'Loading'} />}
            <Shipping
              noShadow={noShadow}
              variant={isPage ? 'page' : 'panel'}
              modeControl={manualShipToggle}
              addressValidationClick={() => setShowAddressPopup(true)}
              autoFulfillerClicked={autoFulfillerClicked}
              fulfillAndNextClicked={fulfillAndNextClicked}
              availableItems={orderItemsWithAlias || []}
              batchOrder={!!batchId}
              contentTypes={contentTypes}
              fpErrorMessage={fpErrorMessage}
              control={control}
              currentProvider={currentProvider}
              createLabel={onSubmit}
              customOrigins={customOrigins}
              customOriginLoading={customOriginLoading}
              disabledCreateLabel={disabledCreateLabel}
              disabledFulfillment={hasMissingHTSCode || !activeShipmentHasStock}
              errors={errors}
              enableAutoPrint={enableAutoPrint}
              fpList={fpList}
              fpLoading={fpLoading}
              shipmentsLoading={isPage && plannedOrderKey !== planningOrderKey}
              fulfilledLabels={fulfilledLabels}
              shipments={shipmentViews}
              activeShipmentId={activeShipmentId}
              unstockedItems={unstockedItems}
              order={data}
              labels={labels}
              returnLabels={returnLabels}
              labelsLoading={labelsLoading}
              trackingStatuses={trackingStatuses}
              onViewLabel={onViewLabel}
              onRefundLabel={onRefundLabel}
              onMoveItem={moveShipmentItem}
              onSplitItem={splitShipmentItem}
              onShipmentProviderChange={shipmentProviderPicked}
              onActivateShipment={setActiveShipmentId}
              onMergeShipments={mergeShipments}
              isItemAvailableAt={isItemAvailableAt}
              fulfillmentProviderChanged={
                isPage ? shipmentProviderChanged : fulfillmentProviderChanged
              }
              rateSelected={rateSelected}
              packageBoxes={packageBoxes}
              forceGeneratePublicDimension={forceGeneratePublicDimension}
              handleSubmit={handleSubmit}
              invalidAddress={invalidAddress}
              addressValidationError={addressValidationError}
              isHazmat={isHazmat}
              addressValidationData={addressValidationData}
              isNotManualFP={isNotManualFP}
              isUSPS={isUSPS}
              isInternational={isInternational}
              metadataLoading={metadataLoading}
              orderError={error}
              packages={packages}
              providerChanged={providerChanged}
              providers={providers}
              providerRates={providerRates}
              qtyChanged={qtyChanged}
              rate={rate}
              rateError={rateError}
              rateCurrency={data?.currency_code}
              rateLoading={rateLoading}
              rateNegotiate={rateNegotiate}
              saving={saving}
              serviceChanged={serviceChanged}
              services={services}
              shippingSpeedData={shippingSpeedData}
              requestedShipping={requestedShipping}
              show={show}
              showRatesByDefault={showRatesByDefault}
              time={time}
              toggleItems={toggleItems}
              uploadShippingLabel={uploadShippingLabel}
              isSignature={isSignature}
              isRFCRequired={isRFCRequired}
              setIsSignature={setIsSignature}
              setValue={setValue}
              ddp={ddp}
            />
            {/* On the details page the order itself is already on screen, so the
                card keeps only the shipping form. */}
            {!isPage && (
              <>
                <ItemOrdered
                  items={
                    CommonUtility.isValidArray(orderItemsWithAlias)
                      ? orderItemsWithAlias
                      : data?.order_contents || []
                  }
                  customItems={data?.custom_items || []}
                  total={(data?.sales_proceeds?.items_total ?? 0) + customTotal}
                  currency={data?.currency_code}
                />
                {data?.customer_details?.name ||
                data?.customer_details?.e_mail ||
                data?.customer_details?.phone ? (
                  <Customer
                    customer={data?.customer_details}
                    addressValidationClick={() => {
                      setManualAddressUpdate(() => true);
                      setShowAddressPopup(() => true);
                    }}
                  />
                ) : (
                  ''
                )}
                <Notes />
                <Summary
                  summary={data?.summary}
                  customTotal={customTotal}
                  salesProceeds={data?.sales_proceeds}
                  marketplace={data?.marketplace}
                  currency={data?.currency_code}
                />
              </>
            )}
          </LabelContainer>
        </form>
      )}

      <PrintLabelPopup
        open={showPrintLabel}
        closeModal={printLabelClose}
        // @ts-expect-error - TS2322: Type '{ open: boolean; closeModal: () => void; url: any; name: any; printURL: an...
        url={generatedLabel?.url}
        // @ts-expect-error - TS2339: Property 'key' does not exist on type 'never'.
        name={generatedLabel?.key}
        // @ts-expect-error - TS2339: Property 'url' does not exist on type 'never'.
        printURL={generatedLabel?.url}
      />

      <UploadShippingLabelPopup
        orderItems={orderItems}
        onClose={closeUploadLabelPopup}
        open={uploadLabelPopup}
        autoFulfillerClicked={autoFulfillerClicked}
        loading={saving}
        qtyChanged={qtyChanged}
        toggleItems={toggleItems}
      />

      <AddressValidationPopup
        open={showAddressPopup}
        closeModal={closeAddressValidationPopup}
        currentAddressData={orderAddress}
        orderId={orderId}
        isValidAddress={isValidAddress}
        addressValidationData={addressValidationData}
        addressValidationError={addressValidationError}
        setFormAddressOverride={setFormAddressOverride}
        notManual={data?.marketplace !== 'manual'}
        isManualUpdate={manualAddressUpdate}
      />
      <AddBalancePopup open={addBalancePopup} closeModal={balancePopupClose} />
      <AddCreditCardPopup
        open={addCreditCardPopup}
        closeModal={creditCardPopupClose}
      />
    </>
  );
}
